import { useCallback, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '@helios/ui';

export interface PageWorkspaceSection {
  /** Stable within the owning page; labels may change without resetting drafts. */
  id: string;
  label: string;
  content: ReactNode;
}

interface PageSectionWorkspaceProps {
  sections: readonly PageWorkspaceSection[];
  header?: ReactNode;
  toolbar?: ReactNode;
  className?: string;
  ariaLabel?: string;
  layout?: 'auto' | 'tabs' | 'grid';
  defaultSectionId?: string;
  activeSectionId?: string;
  onSectionChange?: (id: string) => void;
  minSectionWidth?: number;
  minSectionHeight?: number;
}

/**
 * Explicit page sections share the available viewport. When readable panels no
 * longer fit, named top tabs expose each section instead of shrinking text or
 * clipping data. Bodies retain their own scroll area for long records and zoom.
 */
export function PageSectionWorkspace({
  sections,
  header,
  toolbar,
  className,
  ariaLabel = 'Page sections',
  layout = 'auto',
  defaultSectionId,
  activeSectionId,
  onSectionChange,
  minSectionWidth = 340,
  minSectionHeight = 240,
}: PageSectionWorkspaceProps) {
  const prefix = useId();
  const viewport = useRef<HTMLDivElement>(null);
  const tabStrip = useRef<HTMLDivElement | null>(null);
  const removedTabFocus = useRef<{ control: HTMLElement; panelId: string } | null>(null);
  const registerTabStrip = useCallback((element: HTMLDivElement | null) => {
    // A ref detaches before React removes its DOM. Capture only the tab that
    // actually owns focus at that moment, never a previously selected control.
    if (!element && tabStrip.current) {
      const focused = document.activeElement;
      if (
        focused instanceof HTMLElement &&
        tabStrip.current.contains(focused) &&
        focused.matches('[role="tab"]')
      ) {
        const panelId = focused.getAttribute('aria-controls');
        if (panelId) removedTabFocus.current = { control: focused, panelId };
      }
    }
    tabStrip.current = element;
  }, []);
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);
  const [selected, setSelected] = useState(defaultSectionId ?? sections[0]?.id ?? '');
  const requested = activeSectionId ?? selected;
  const active = sections.find((section) => section.id === requested)?.id ?? sections[0]?.id;

  useLayoutEffect(() => {
    const element = viewport.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setSize((current) =>
        current?.width === width && current.height === height ? current : { width, height },
      );
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const columns = Math.max(
    1,
    Math.min(
      sections.length,
      Math.floor(((size?.width ?? minSectionWidth) + 12) / (minSectionWidth + 12)),
    ),
  );
  const rows = Math.ceil(sections.length / columns);
  const phoneViewport =
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;
  const stacked = size !== null && phoneViewport && layout !== 'tabs';
  const fits = size === null || size.height >= rows * minSectionHeight + Math.max(0, rows - 1) * 12;
  const tabbed =
    sections.length > 1 && !stacked && (layout === 'tabs' || (layout === 'auto' && !fits));

  useLayoutEffect(() => {
    if (tabbed) return;
    const previous = removedTabFocus.current;
    removedTabFocus.current = null;
    if (!previous || previous.control.isConnected) return;
    // Another autofocus or deliberate move to app chrome/content wins.
    if (document.activeElement && document.activeElement !== document.body) return;
    const panel = document.getElementById(previous.panelId);
    if (panel && viewport.current?.contains(panel) && !panel.hidden)
      panel.focus({ preventScroll: !stacked });
  }, [tabbed, stacked]);

  const select = (id: string) => {
    if (activeSectionId === undefined) setSelected(id);
    onSectionChange?.(id);
  };

  return (
    <div
      className={cn(
        'flex h-full min-h-0 min-w-0 flex-1 flex-col gap-2 overflow-hidden [@media(max-height:600px)]:overflow-auto',
        className,
      )}
      data-page-section-workspace=""
    >
      {header ? <div className="shrink-0">{header}</div> : null}
      {toolbar ? <div className="shrink-0">{toolbar}</div> : null}
      <div
        ref={viewport}
        className="flex min-h-0 min-w-0 flex-1 flex-col gap-2 overflow-hidden [@media(max-height:600px)]:min-h-[240px]"
      >
        {tabbed ? (
          <div
            ref={registerTabStrip}
            role="tablist"
            aria-label={ariaLabel}
            className="border-border flex shrink-0 gap-1 overflow-x-auto border-b pb-px"
            onKeyDown={(event) => {
              const controls = Array.from(
                event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]'),
              );
              const index = controls.indexOf(event.target as HTMLButtonElement);
              if (index < 0) return;
              const next =
                event.key === 'ArrowRight'
                  ? (index + 1) % sections.length
                  : event.key === 'ArrowLeft'
                    ? (index - 1 + sections.length) % sections.length
                    : event.key === 'Home'
                      ? 0
                      : event.key === 'End'
                        ? sections.length - 1
                        : null;
              if (next === null || !sections[next]) return;
              event.preventDefault();
              select(sections[next].id);
              controls[next]?.focus();
            }}
          >
            {sections.map((section) => (
              <button
                key={section.id}
                id={`${prefix}-tab-${section.id}`}
                type="button"
                role="tab"
                aria-selected={section.id === active}
                aria-controls={`${prefix}-panel-${section.id}`}
                tabIndex={section.id === active ? 0 : -1}
                onClick={() => select(section.id)}
                className={cn(
                  'hx-section-tab focus-visible:ring-ring relative whitespace-nowrap rounded-t-md px-3 py-1.5 text-xs font-semibold focus-visible:outline-none focus-visible:ring-2',
                  section.id === active
                    ? 'bg-card text-brand-gold after:bg-brand-gold after:absolute after:inset-x-2 after:bottom-0 after:h-0.5'
                    : 'text-muted-foreground hover:bg-secondary/50 hover:text-foreground',
                )}
              >
                {section.label}
              </button>
            ))}
          </div>
        ) : null}
        <div
          className={cn(
            'min-h-0 min-w-0 flex-1 gap-3',
            stacked ? 'flex flex-col overflow-auto' : 'grid overflow-hidden',
          )}
          style={
            stacked
              ? undefined
              : {
                  gridTemplateColumns: `repeat(${tabbed ? 1 : columns}, minmax(0, 1fr))`,
                  gridTemplateRows: `repeat(${tabbed ? 1 : rows}, minmax(0, 1fr))`,
                }
          }
          data-section-layout={stacked ? 'stack' : tabbed ? 'tabs' : 'grid'}
        >
          {sections.map((section, index) => {
            const shown = !tabbed || section.id === active;
            const remaining = sections.length % columns;
            return (
              <section
                key={section.id}
                id={`${prefix}-panel-${section.id}`}
                role={tabbed ? 'tabpanel' : 'region'}
                aria-labelledby={tabbed ? `${prefix}-tab-${section.id}` : undefined}
                aria-label={tabbed ? undefined : section.label}
                hidden={!shown}
                tabIndex={0}
                onFocusCapture={() => {
                  // Keep the panel being edited visible if resize/zoom changes
                  // the balanced grid into tabs while a field has focus.
                  if (!tabbed && section.id !== active) select(section.id);
                }}
                style={{
                  display: shown ? undefined : 'none',
                  gridColumn:
                    !tabbed && !stacked && remaining !== 0 && index === sections.length - 1
                      ? `span ${columns - remaining + 1}`
                      : undefined,
                }}
                className={cn(
                  'focus-visible:ring-ring min-h-0 min-w-0 overflow-auto rounded-lg focus-visible:outline-none focus-visible:ring-2 [&>*]:min-w-0',
                  stacked ? 'shrink-0' : '[&>*:only-child]:min-h-full',
                )}
              >
                {section.content}
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}
